/**
 * Frozen roll algorithm for challenge version 1.4.6.
 *
 * DO NOT EDIT once this version is live on-chain.
 * To change roll behavior, copy this file forward to the next version and edit only that copy.
 *
 * Compare with the previous version:
 *   diff -u www/packages/react-app/src/utils/rollers/<prev>.mjs www/packages/react-app/src/utils/rollers/v1_4_6.mjs
 */
import { getRandomItem, getRandomItemWeighted, seedRandomItem } from '../rollRng.mjs';

export const VERSION = '1.4.6';

/**
 * @param {string} seed block.hash
 * @param {object} challengeConfigVersion
 * @returns {{ version: string, mission: object, loadout: object, success: object }}
 */

export function roll(seed, challengeConfigVersion) {
  seedRandomItem(seed);
  const version = VERSION;

const selectedEnemy = getRandomItem(challengeConfigVersion.enemy);
const selectedTeamplay = getRandomItemWeighted(challengeConfigVersion.teamplay, challengeConfigVersion.teamplayWeights);

let selectedDifficulty = getRandomItemWeighted(challengeConfigVersion.difficulty, challengeConfigVersion.difficultyWeights);
while (!isValidDifficulty(selectedTeamplay, selectedDifficulty)) {
  selectedDifficulty = getRandomItemWeighted(challengeConfigVersion.difficulty, challengeConfigVersion.difficultyWeights);
}

const selectedDuration = getRandomItemWeighted(challengeConfigVersion.duration, challengeConfigVersion.durationWeights);

let selectedStars = getRandomItem(challengeConfigVersion.stars);
while (!isValidStars(selectedDifficulty, selectedStars)) {
  selectedStars = getRandomItem(challengeConfigVersion.stars);
}

let selectedPrimary, selectedSecondary, selectedGrenade,
selectedStratagem1, selectedStratagem2, selectedStratagem3, selectedStratagem4;

do {
  selectedPrimary = getRandomItem(challengeConfigVersion.primary);

  selectedSecondary = getRandomItem(challengeConfigVersion.secondary);
  while (!isValidSecondary(selectedTeamplay, selectedSecondary, challengeConfigVersion, selectedPrimary)) {
    selectedSecondary = getRandomItem(challengeConfigVersion.secondary);
  }

  selectedGrenade = getRandomItem(challengeConfigVersion.grenade);
  while (!isValidGrenade(selectedGrenade, challengeConfigVersion, selectedPrimary, selectedSecondary)) {
    selectedGrenade = getRandomItem(challengeConfigVersion.grenade);
  }

  // IMPORTANT: keep historical version roll paths immutable.
  // Any structural RNG change must go into a NEW version branch only.
  selectedStratagem1 = getRandomItem(challengeConfigVersion.SupportWeaponStratagems);
  while (!isValidStratagem([], selectedStratagem1, challengeConfigVersion, selectedEnemy, selectedTeamplay, selectedSecondary, selectedPrimary, selectedGrenade)) {
    selectedStratagem1 = getRandomItem(challengeConfigVersion.SupportWeaponStratagems);
  }

  selectedStratagem2 = getRandomItemWeighted(challengeConfigVersion.stratagem, challengeConfigVersion.stratagemWeights);
  while (!isValidStratagem([selectedStratagem1], selectedStratagem2, challengeConfigVersion, selectedEnemy, selectedTeamplay, selectedSecondary, selectedPrimary, selectedGrenade)) {
    selectedStratagem2 = getRandomItemWeighted(challengeConfigVersion.stratagem, challengeConfigVersion.stratagemWeights);
  }

  selectedStratagem3 = getRandomItemWeighted(challengeConfigVersion.stratagem, challengeConfigVersion.stratagemWeights);
  while (!isValidStratagem([selectedStratagem1, selectedStratagem2], selectedStratagem3, challengeConfigVersion, selectedEnemy, selectedTeamplay, selectedSecondary, selectedPrimary, selectedGrenade)) {
    selectedStratagem3 = getRandomItemWeighted(challengeConfigVersion.stratagem, challengeConfigVersion.stratagemWeights);
  }

  selectedStratagem4 = getRandomItemWeighted(challengeConfigVersion.stratagem, challengeConfigVersion.stratagemWeights);
  while (!isValidStratagem([selectedStratagem1, selectedStratagem2, selectedStratagem3], selectedStratagem4, challengeConfigVersion, selectedEnemy, selectedTeamplay, selectedSecondary, selectedPrimary, selectedGrenade)) {
    selectedStratagem4 = getRandomItemWeighted(challengeConfigVersion.stratagem, challengeConfigVersion.stratagemWeights);
  }
} while (!hazDaBomb(
  selectedEnemy,
  selectedPrimary,
  selectedSecondary,
  selectedGrenade,
  selectedStratagem1,
  selectedStratagem2,
  selectedStratagem3,
  selectedStratagem4,
  challengeConfigVersion
));

return {
  version,
  mission: {
    enemy: selectedEnemy,
    difficulty: selectedDifficulty,
    duration: selectedDuration,
    teamplay: selectedTeamplay,
    stars: selectedStars,
  },
  loadout: {
    primary: selectedPrimary,
    secondary: selectedSecondary,
    grenade: selectedGrenade,
    stratagem1: selectedStratagem1,
    stratagem2: selectedStratagem2,
    stratagem3: selectedStratagem3,
    stratagem4: selectedStratagem4,
  },
  success: {
    stars: selectedStars,
  },
};
}

function isValidStratagem(selected, strat, config, selectedEnemy, selectedTeamplay, selectedSecondary, selectedPrimary, selectedGrenade) {
  // ensure we don't have duplicate slot stratagems
  for (let i = 0; i < selected.length; i++) {
    if ((config.BackpackStratagems.includes(selected[i]) && config.BackpackStratagems.includes(strat)) ||
        (config.SupportWeaponStratagems.includes(selected[i]) && config.SupportWeaponStratagems.includes(strat)) ||
        (config.SmokeWeapons.includes(selected[i]) && config.SmokeWeapons.includes(strat)) ||
        ((config.VehicleStratagems || config.MechStratagems) && (config.VehicleStratagems || config.MechStratagems).includes(selected[i]) && (config.VehicleStratagems || config.MechStratagems).includes(strat)) ||
        selected[i] === strat) {
      return false;
    }
  }

  // Bastion tank is a 4-crew vehicle; exclude in solo
  if (selectedTeamplay === "Solo" && strat === "TD-220 Bastion MK XVI") {
    return false;
  }

  // ensure we don't have shields that can't stop mele attacks on terminid missions
  if ((selectedEnemy === "Terminid" && strat === "FX-12 Shield Generator Relay") ||
      (selectedEnemy === "Terminid" && strat === "SH-51 Directional Shield")) {
    return false;
  }

  // if we rull a shield backpack, ensure we have a one handed weapon
  let hasOneHandedWeapon = false;
  for (let i = 0; i < selected.length; i++) {
    if (config.OneHandedWeapons.includes(selected[i]) ||
        config.OneHandedWeapons.includes(selectedPrimary) ||
        config.OneHandedWeapons.includes(selectedSecondary)) {
      hasOneHandedWeapon = true;
    }
  }

  // no shields if we don't have a one handed weapon or if we have a stim pistol
  if (strat === "SH-51 Directional Shield" || strat === "SH-20 Balistic Shield Backpack") {
    if (selectedSecondary === "P-11 Stim Pistol" || !hasOneHandedWeapon) {
      return false;
    }
  }

  // ensure we only have one melee weapon
  if ((config.MeleeWeapons.includes(strat) && config.MeleeWeapons.includes(selectedPrimary)) ||
      (config.MeleeWeapons.includes(strat) && config.MeleeWeapons.includes(selectedSecondary))) {
    return false;
  }

  // ensure we only have one smoke weapon (paired with check in duplicate stratagems)
  if (config.SmokeWeapons.includes(strat) && config.SmokeWeapons.includes(selectedGrenade)) {
    return false;
  }

  return true;
}

function isValidStars(difficulty, stars) {
  if ((difficulty === "1 - Trivial or greater"      && stars === "3 stars or more") ||
      (difficulty === "1 - Trivial or greater"      && stars === "4 stars or more") ||
      (difficulty === "1 - Trivial or greater"      && stars === "5 stars") ||
      (difficulty === "2 - Easy or greater"         && stars === "3 stars or more") ||
      (difficulty === "2 - Easy or greater"         && stars === "4 stars or more") ||
      (difficulty === "2 - Easy or greater"         && stars === "5 stars") ||
      (difficulty === "3 - Medium or greater"       && stars === "4 stars or more") ||
      (difficulty === "3 - Medium or greater"       && stars === "5 stars") ||
      (difficulty === "4 - Challenging or greater"  && stars === "4 stars or more") ||
      (difficulty === "4 - Challenging or greater"  && stars === "5 stars") ||
      (difficulty === "5 - Hard or greater"         && stars === "5 stars") ||
      (difficulty === "6 - Extreme or greater"      && stars === "5 stars")) {
    return false;
  }

  return true;
}

function isValidDifficulty(teamplay, difficulty) {
  if ((teamplay === "Squad" && difficulty === "1 - Trivial or greater") ||
      (teamplay === "Squad" && difficulty === "2 - Easy or greater") ||
      (teamplay === "Squad" && difficulty === "3 - Medium or greater") ||
      (teamplay === "Squad" && difficulty === "4 - Challenging or greater") ||
      (teamplay === "Squad" && difficulty === "5 - Hard or greater")) {
    return false;
  }

  return true;
}

function isValidSecondary(teamplay, secondary, config, selectedPrimary) {
  if ((teamplay === "Solo" && secondary === "P-11 Stim Pistol")) {
    return false;
  }

  // Onlyu have one melee weapon
  if (config.MeleeWeapons.includes(secondary) && config.MeleeWeapons.includes(selectedPrimary)) {
    return false;
  }

  return true;
}

function isValidGrenade(grenade, config, selectedPrimary, selectedSecondary) {
  // Only have one melee weapon
  if ((config.MeleeWeapons.includes(grenade) && config.MeleeWeapons.includes(selectedPrimary)) ||
      (config.MeleeWeapons.includes(grenade) && config.MeleeWeapons.includes(selectedSecondary))) {
    return false;
  }

  return true;
}

// this fuction ensures that there is always an answer to close an enemy spawner
function hazDaBomb(enemy, primary, secondary, grenade, strat1, strat2, strat3, strat4, config) {
  const stratagems = [strat1, strat2, strat3, strat4];

  // Check if any weapon can close enemy spawners using config data
  if (config.SpawnerWeapons && config.SpawnerWeapons[enemy]) {
    const factionWeapons = config.SpawnerWeapons[enemy];

    if (factionWeapons.primary && factionWeapons.primary.includes(primary)) {
      return true;
    }

    if (factionWeapons.secondary && factionWeapons.secondary.includes(secondary)) {
      return true;
    }

    if (factionWeapons.grenade && factionWeapons.grenade.includes(grenade)) {
      return true;
    }

    if (factionWeapons.stratagem) {
      for (const strat of stratagems) {
        if (factionWeapons.stratagem.includes(strat)) {
          return true;
        }
      }
    }
  }

  return false;
}
